"use client"

import { useState, useEffect, Suspense } from "react"
import { useGoogleReCaptcha } from "@/lib/recaptcha-client"
import { useSearchParams, usePathname } from "next/navigation"
import { motion } from "framer-motion"
import { Section } from "@/components/layout/Section"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Button } from "@/components/ui/button"
import { FileText, Send, Upload, Briefcase } from "lucide-react"
import Link from "next/link"
import { CTA } from "@/components/sections/CTA"
import { isValidLocale, type Locale } from "@/lib/i18n"
import { getTranslations } from "@/lib/translations"

const positionMap: Record<string, string> = {
  "Senior Frontend Developer": "senior-frontend-developer",
  "UI/UX Designer": "ui-ux-designer",
  "Mobile App Developer": "mobile-app-developer",
  "AI/ML Engineer": "ai-ml-engineer",
}

function CareerApplicationForm() {
  const searchParams = useSearchParams()
  const pathname = usePathname()
  const positionParam = searchParams.get("position")

  // Get current locale from pathname
  const getCurrentLocale = (): Locale => {
    const segments = pathname.split('/').filter(Boolean)
    const firstSegment = segments[0]
    return isValidLocale(firstSegment) ? firstSegment : 'tr'
  }

  const currentLocale = getCurrentLocale()
  const t = getTranslations(currentLocale)
  const { executeRecaptcha } = useGoogleReCaptcha()

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    position: "",
    experience: "",
    linkedin: "",
    portfolio: "",
    coverLetter: "",
  })

  const [resume, setResume] = useState<File | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [submitSuccess, setSubmitSuccess] = useState(false)

  useEffect(() => {
    if (positionParam && positionMap[positionParam]) {
      setFormData((prev) => ({ ...prev, position: positionParam }))
    }
  }, [positionParam])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setResume(e.target.files[0])
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitting(true)
    setSubmitError(null)
    setSubmitSuccess(false)

    try {
      // Get reCAPTCHA token
      let recaptchaToken = ""
      if (executeRecaptcha) {
        recaptchaToken = await executeRecaptcha("career_form")
      }

      const formDataToSend = new FormData()
      formDataToSend.append("name", formData.name)
      formDataToSend.append("email", formData.email)
      formDataToSend.append("phone", formData.phone)
      formDataToSend.append("position", formData.position)
      formDataToSend.append("experience", formData.experience)
      formDataToSend.append("linkedin", formData.linkedin)
      formDataToSend.append("portfolio", formData.portfolio)
      formDataToSend.append("coverLetter", formData.coverLetter)
      formDataToSend.append("recaptchaToken", recaptchaToken)
      if (resume) {
        formDataToSend.append("resumeFile", resume)
      }

      const response = await fetch("/api/career/apply", {
        method: "POST",
        body: formDataToSend,
      })

      if (response.ok) {
        setSubmitSuccess(true)
        setFormData({
          name: "",
          email: "",
          phone: "",
          position: "",
          experience: "",
          linkedin: "",
          portfolio: "",
          coverLetter: "",
        })
        setResume(null)
      } else {
        const data = await response.json()
        setSubmitError((data as any)?.error || t.pages.careerApplication.messages.submitError)
      }
    } catch (error) {
      setSubmitError(t.pages.careerApplication.messages.submitError)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <>
      {/* Hero Section */}
      <Section className="relative overflow-hidden bg-gradient-to-br from-[rgb(3,74,166)] via-[rgb(3,74,166)] to-[rgb(2,40,89)] text-white pt-24 pb-16 sm:pt-32 sm:pb-24">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(255,255,255,0.1),transparent_70%)]" />
        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex p-4 bg-white/10 backdrop-blur-sm rounded-2xl mb-8"
          >
            <Briefcase className="h-12 w-12 sm:h-16 sm:w-16" />
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight mb-6 font-mono"
          >
            {t.pages.careerApplication.hero.title}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-lg sm:text-xl text-white/90 max-w-2xl mx-auto"
          >
            {t.pages.careerApplication.hero.subtitle}
          </motion.p>
        </div>
      </Section>

      {/* Application Form */}
      <Section className="py-16 lg:py-24 bg-white">
        <div className="max-w-4xl mx-auto">
          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Personal Information */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="bg-slate-50 rounded-xl p-6 sm:p-8"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 bg-[rgb(3,74,166)]/10 rounded-lg">
                  <FileText className="h-5 w-5 text-[rgb(3,74,166)]" />
                </div>
                <h2 className="text-xl font-bold font-mono">{t.pages.careerApplication.sections.personalInfo}</h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">{t.pages.careerApplication.fields.name} *</label>
                  <Input
                    type="text"
                    name="name"
                    placeholder={t.pages.careerApplication.fields.name}
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="h-12 border-slate-300 focus:border-[rgb(3,74,166)] focus:ring-[rgb(3,74,166)]"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">{t.pages.careerApplication.fields.email} *</label>
                  <Input
                    type="email"
                    name="email"
                    placeholder="ornek@email.com"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="h-12 border-slate-300 focus:border-[rgb(3,74,166)] focus:ring-[rgb(3,74,166)]"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">{t.pages.careerApplication.fields.phone} *</label>
                  <Input
                    type="tel"
                    name="phone"
                    placeholder="05XX XXX XX XX"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="h-12 border-slate-300 focus:border-[rgb(3,74,166)] focus:ring-[rgb(3,74,166)]"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">{t.pages.careerApplication.fields.position} *</label>
                  <select
                    name="position"
                    value={formData.position}
                    onChange={handleChange}
                    required
                    className="w-full h-12 px-3 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[rgb(3,74,166)] focus:border-[rgb(3,74,166)]"
                  >
                    <option value="">{t.pages.careerApplication.fields.selectPosition}</option>
                    <option value="Senior Frontend Developer">Senior Frontend Developer</option>
                    <option value="UI/UX Designer">UI/UX Designer</option>
                    <option value="Mobile App Developer">Mobile App Developer</option>
                    <option value="AI/ML Engineer">AI/ML Engineer</option>
                  </select>
                </div>
              </div>
            </motion.div>

            {/* Professional Information */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="bg-slate-50 rounded-xl p-6 sm:p-8"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 bg-[rgb(3,74,166)]/10 rounded-lg">
                  <Briefcase className="h-5 w-5 text-[rgb(3,74,166)]" />
                </div>
                <h2 className="text-xl font-bold font-mono">{t.pages.careerApplication.sections.professionalInfo}</h2>
              </div>
              <div className="space-y-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">{t.pages.careerApplication.fields.experience}</label>
                  <Input
                    type="text"
                    name="experience"
                    placeholder={t.pages.careerApplication.fields.experience}
                    value={formData.experience}
                    onChange={handleChange}
                    className="h-12 border-slate-300 focus:border-[rgb(3,74,166)] focus:ring-[rgb(3,74,166)]"
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">{t.pages.careerApplication.fields.linkedin}</label>
                    <Input
                      type="url"
                      name="linkedin"
                      placeholder="https://linkedin.com/in/..."
                      value={formData.linkedin}
                      onChange={handleChange}
                      className="h-12 border-slate-300 focus:border-[rgb(3,74,166)] focus:ring-[rgb(3,74,166)]"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">{t.pages.careerApplication.fields.portfolio}</label>
                    <Input
                      type="url"
                      name="portfolio"
                      placeholder="https://..."
                      value={formData.portfolio}
                      onChange={handleChange}
                      className="h-12 border-slate-300 focus:border-[rgb(3,74,166)] focus:ring-[rgb(3,74,166)]"
                    />
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Resume Upload */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="bg-slate-50 rounded-xl p-6 sm:p-8"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 bg-[rgb(3,74,166)]/10 rounded-lg">
                  <Upload className="h-5 w-5 text-[rgb(3,74,166)]" />
                </div>
                <h2 className="text-xl font-bold font-mono">{t.pages.careerApplication.sections.resume}</h2>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">{t.pages.careerApplication.fields.resumeUpload} *</label>
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-300 rounded-md cursor-pointer hover:bg-slate-50 transition-colors">
                    <Upload className="h-5 w-5 text-slate-600" />
                      <span className="text-sm text-slate-700">{t.pages.careerApplication.fields.resumePlaceholder}</span>
                    <input
                      type="file"
                      accept=".pdf,.doc,.docx"
                      onChange={handleFileChange}
                      required
                      className="hidden"
                    />
                  </label>
                  {resume && (
                    <span className="text-sm text-slate-600 flex items-center gap-2">
                      <FileText className="h-4 w-4" />
                      {resume.name}
                    </span>
                  )}
                </div>
                <p className="text-xs text-slate-500">{t.pages.careerApplication.fields.resumeMaxSize}</p>
              </div>
            </motion.div>

            {/* Cover Letter */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="bg-slate-50 rounded-xl p-6 sm:p-8"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 bg-[rgb(3,74,166)]/10 rounded-lg">
                  <FileText className="h-5 w-5 text-[rgb(3,74,166)]" />
                </div>
                <h2 className="text-xl font-bold font-mono">{t.pages.careerApplication.sections.coverLetter}</h2>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">{t.pages.careerApplication.fields.coverLetterLabel}</label>
                <Textarea
                  name="coverLetter"
                  placeholder={t.pages.careerApplication.fields.coverLetterPlaceholder}
                  value={formData.coverLetter}
                  onChange={handleChange}
                  rows={6}
                  className="border-slate-300 focus:border-[rgb(3,74,166)] focus:ring-[rgb(3,74,166)]"
                />
              </div>
            </motion.div>

            {/* Submit Button */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.4 }}
              className="flex flex-col sm:flex-row items-center justify-between gap-4"
            >
              <Link
                href={`/${currentLocale}/${currentLocale === 'tr' ? 'kariyer' : 'career'}`}
                className="text-sm text-slate-600 hover:text-[rgb(3,74,166)] transition-colors"
              >
                ← {currentLocale === 'tr' ? 'Kariyer sayfasına dön' : 'Back to career page'}
              </Link>
              <Button
                type="submit"
                size="lg"
                disabled={submitting}
                className="bg-[rgb(3,74,166)] hover:bg-[rgb(2,40,89)] text-white px-8"
              >
                {submitting ? (
                  <>
                    <span className="animate-spin mr-2">⏳</span>
                    {t.pages.careerApplication.buttons.submitting}
                  </>
                ) : (
                  <>
                    <Send className="h-5 w-5 mr-2" />
                    {t.pages.careerApplication.buttons.submit}
                  </>
                )}
              </Button>
            </motion.div>

            {/* Error Message */}
            {submitError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 text-sm">
                {submitError}
              </div>
            )}

            {/* Success Message */}
            {submitSuccess && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-green-700 text-sm">
                {t.pages.careerApplication.messages.submitSuccess}
              </div>
            )}
          </form>
        </div>
      </Section>
      <CTA locale={currentLocale} />
    </>
  )
}

export default function CareerApplicationPage() {
  return (
    <Suspense fallback={<div>Loading...</div>}>
      <CareerApplicationForm />
    </Suspense>
  )
}

